if (!customElements.get('event-questions')) {
  customElements.define(
    'event-questions',
    class EventQuestions extends HTMLElement {
      constructor() {
        super();

        this.formId = this.dataset.formId;
        this.dietaryLabel = this.dataset.dietaryLabel;
        this.guestLabel = this.dataset.guestLabel;
        this.bagLabel = this.dataset.bagLabel;
        this.quantityInput = this.querySelector('[data-event-quantity]');
        this.bagOptions = Array.from(this.querySelectorAll('[data-bag-option]'));
        this.ticketList = this.querySelector('[data-ticket-list]');
        this.ticketTemplate = this.querySelector('[data-ticket-template]');
        this.ticketsHeading = this.querySelector('[data-tickets-heading]');
        // Every ticket card ever created, so answers survive the quantity going down and back up
        this.tickets = [];
        this.onSubmit = this.onSubmit.bind(this);

        this.bagOptions.forEach((option) => option.addEventListener('change', () => this.toggleError(this, 'bag', false)));
        this.quantityInput.addEventListener('change', this.renderTickets.bind(this));
        this.quantityInput.addEventListener('input', this.renderTickets.bind(this));

        this.renderTickets();
      }

      connectedCallback() {
        // Capture phase on document runs before product-form's own submit handler
        document.addEventListener('submit', this.onSubmit, true);
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this.onSubmit, true);
      }

      getQuantity() {
        const max = parseInt(this.quantityInput.max) || 1;
        const quantity = parseInt(this.quantityInput.value) || 1;
        return Math.min(Math.max(quantity, 1), max);
      }

      getTickets() {
        return Array.from(this.ticketList.children);
      }

      renderTickets() {
        const count = this.getQuantity();
        const previous = this.ticketList.children.length;
        if (count === previous) return;

        while (this.ticketList.children.length > count) this.ticketList.lastElementChild.remove();
        while (this.ticketList.children.length < count) {
          const index = this.ticketList.children.length;
          if (!this.tickets[index]) this.tickets[index] = this.createTicket(index + 1);
          this.ticketList.appendChild(this.tickets[index]);
        }

        const single = count === 1;
        const tickets = this.getTickets();
        // Nothing to ask per ticket (e.g. no dietary question and no guest names), so hide the whole list
        this.ticketList.parentElement.hidden = tickets.every((ticket) => ticket.hidden);
        this.ticketList.classList.toggle('is-single', single);
        this.ticketsHeading.hidden = single;

        tickets.forEach((ticket, index) => {
          const next = ticket.querySelector('[data-ticket-next]');
          next.hidden = index === count - 1;
          next.textContent = `Next: Ticket ${index + 2}`;
        });

        if (single) {
          this.openTicket(tickets[0]);
        } else if (count > previous) {
          this.openTicket(tickets.find((ticket) => !this.isComplete(ticket)) || tickets[tickets.length - 1]);
        }
      }

      createTicket(number) {
        const ticket = this.ticketTemplate.content.firstElementChild.cloneNode(true);
        const id = `${this.formId}-ticket-${number}`;
        const isGuest = number > 1;
        const header = ticket.querySelector('[data-ticket-toggle]');
        const body = ticket.querySelector('[data-ticket-body]');
        const nameWrapper = ticket.querySelector('[data-ticket-name-wrapper]');

        ticket.querySelector('[data-ticket-title]').textContent = `Ticket ${number} · ${isGuest ? 'Guest' : 'You'}`;
        header.id = `${id}-header`;
        header.setAttribute('aria-controls', `${id}-body`);
        body.id = `${id}-body`;
        body.setAttribute('role', 'region');
        body.setAttribute('aria-labelledby', header.id);
        ticket.querySelectorAll('[data-error]').forEach((error) => {
          error.id = `${id}-${error.dataset.error}-error`;
        });

        if (isGuest && nameWrapper) {
          const nameInput = nameWrapper.querySelector('input');
          nameInput.id = `${id}-name`;
          nameInput.setAttribute('aria-describedby', `${id}-name-error`);
          nameWrapper.querySelector('label').htmlFor = nameInput.id;
          nameInput.addEventListener('input', () => {
            this.toggleError(ticket, 'name', false);
            this.updateTicket(ticket);
          });
        } else {
          nameWrapper?.remove();
        }

        // The dietary question is left out of the markup when the event doesn't ask for it
        const hasDietary = Boolean(ticket.querySelector('[data-group="answer"]'));
        if (hasDietary) this.setupDietary(ticket, id);

        ticket.hidden = !hasDietary && !(isGuest && nameWrapper);

        header.addEventListener('click', () => {
          if (header.getAttribute('aria-expanded') === 'true') {
            this.setExpanded(ticket, false);
          } else {
            this.openTicket(ticket);
          }
        });

        ticket.querySelector('[data-ticket-next]').addEventListener('click', () => {
          const invalid = this.validateTicket(ticket);
          if (invalid) {
            invalid.focus();
            return;
          }
          const next = ticket.nextElementSibling;
          if (!next) return;
          this.openTicket(next);
          (next.querySelector('[data-ticket-name]') || next.querySelector('[data-dietary-answer]'))?.focus();
        });

        this.updateTicket(ticket);
        return ticket;
      }

      setupDietary(ticket, id) {
        const answers = Array.from(ticket.querySelectorAll('[data-dietary-answer]'));
        const options = Array.from(ticket.querySelectorAll('[data-dietary-option]'));
        const optionsWrapper = ticket.querySelector('[data-dietary-options]');
        const otherOption = ticket.querySelector('[data-dietary-option][data-other]');
        const otherWrapper = ticket.querySelector('[data-dietary-other]');
        const otherInput = ticket.querySelector('[data-dietary-other-input]');

        ticket.querySelector('[data-group="answer"]').setAttribute('aria-describedby', `${id}-answer-error`);
        answers.forEach((answer) => {
          answer.name = `${id}-dietary`;
          answer.addEventListener('change', () => {
            optionsWrapper.hidden = answer.value !== 'yes';
            this.toggleError(ticket, 'answer', false);
            this.updateTicket(ticket);
          });
        });

        otherInput.id = `${id}-other`;
        otherInput.setAttribute('aria-describedby', `${id}-other-error`);
        otherWrapper.querySelector('label').htmlFor = otherInput.id;

        options.forEach((option) => {
          option.addEventListener('change', () => {
            if (otherOption) {
              otherWrapper.hidden = !otherOption.checked;
              if (option === otherOption && option.checked) otherInput.focus();
              if (!otherOption.checked) this.toggleError(ticket, 'other', false);
            }
            this.toggleError(ticket, 'options', false);
            this.updateTicket(ticket);
          });
        });

        otherInput.addEventListener('input', () => {
          this.toggleError(ticket, 'other', false);
          this.updateTicket(ticket);
        });
      }

      openTicket(ticket) {
        this.getTickets().forEach((item) => this.setExpanded(item, item === ticket));
      }

      setExpanded(ticket, open) {
        // A single ticket has no header, so it always stays open
        if (!open && this.ticketList.classList.contains('is-single')) return;
        ticket.querySelector('[data-ticket-toggle]').setAttribute('aria-expanded', open);
        ticket.querySelector('[data-ticket-body]').hidden = !open;
        ticket.classList.toggle('is-open', open);
      }

      // Dietary answer as saved on the order, e.g. "Vegan, Other: no mushrooms"
      getDietary(ticket) {
        const answer = ticket.querySelector('[data-dietary-answer]:checked')?.value;
        if (!answer) return '';
        if (answer === 'no') return 'No dietary requirements';

        const otherText = ticket.querySelector('[data-dietary-other-input]').value.trim();
        return Array.from(ticket.querySelectorAll('[data-dietary-option]:checked'))
          .map((option) => ('other' in option.dataset && otherText ? `${option.value}: ${otherText}` : option.value))
          .join(', ');
      }

      updateTicket(ticket) {
        const name = ticket.querySelector('[data-ticket-name]')?.value.trim();
        const text = [name, this.getDietary(ticket)].filter(Boolean).join(' · ');
        const complete = this.isComplete(ticket);

        ticket.querySelector('[data-ticket-summary]').textContent = complete ? text : 'Not completed yet';
        ticket.classList.toggle('is-complete', complete);
      }

      isComplete(ticket) {
        return this.getTicketErrors(ticket).length === 0;
      }

      // Returns [error name, element to focus] pairs for every unanswered question on a ticket
      getTicketErrors(ticket) {
        const errors = [];
        const nameInput = ticket.querySelector('[data-ticket-name]');
        if (nameInput && !nameInput.value.trim()) errors.push(['name', nameInput]);

        const answer = ticket.querySelector('[data-dietary-answer]:checked');
        if (!ticket.querySelector('[data-group="answer"]')) return errors;

        if (!answer) {
          errors.push(['answer', ticket.querySelector('[data-dietary-answer]')]);
        } else if (answer.value === 'yes') {
          const options = Array.from(ticket.querySelectorAll('[data-dietary-option]'));
          if (!options.some((option) => option.checked)) errors.push(['options', options[0]]);

          const otherOption = ticket.querySelector('[data-dietary-option][data-other]');
          const otherInput = ticket.querySelector('[data-dietary-other-input]');
          if (otherOption?.checked && !otherInput.value.trim()) errors.push(['other', otherInput]);
        }

        return errors;
      }

      // Shows the ticket's errors and returns the first element to focus, or null when complete
      validateTicket(ticket) {
        const errors = this.getTicketErrors(ticket);
        ['name', 'answer', 'options', 'other'].forEach((name) => {
          this.toggleError(
            ticket,
            name,
            errors.some(([errorName]) => errorName === name)
          );
        });
        this.updateTicket(ticket);
        return errors[0]?.[1] || null;
      }

      onSubmit(event) {
        // getAttribute, because form.id returns the variant `id` input rather than the form's id
        if (event.target.getAttribute('id') !== this.formId) return;

        const firstInvalid = this.validate();
        if (!firstInvalid) {
          this.writeItems();
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        if (firstInvalid.ticket) this.openTicket(firstInvalid.ticket);
        firstInvalid.element.focus();
      }

      // Each ticket is its own cart line (quantity 1) with the same property keys every time, so the
      // order can be exported as one row per guest. The `Ticket` property keeps identical answers from merging.
      writeItems() {
        this.querySelectorAll('[data-item-input]').forEach((input) => input.remove());

        const variantId = document.getElementById(this.formId).querySelector('[name=id]').value;
        const bag = this.bagOptions.find((option) => option.checked)?.value;
        const tickets = this.getTickets();

        tickets.forEach((ticket, index) => {
          const properties = {
            Ticket: `${index + 1} of ${tickets.length}`,
            [this.guestLabel]: ticket.querySelector('[data-ticket-name]')?.value.trim(),
            [this.dietaryLabel]: this.getDietary(ticket),
            [this.bagLabel]: bag,
          };
          const fields = { [`items[${index}][id]`]: variantId, [`items[${index}][quantity]`]: 1 };
          Object.entries(properties).forEach(([key, value]) => {
            if (value) fields[`items[${index}][properties][${key}]`] = value;
          });

          Object.entries(fields).forEach(([name, value]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            input.setAttribute('form', this.formId);
            input.dataset.itemInput = '';
            this.appendChild(input);
          });
        });
      }

      // Returns the first invalid { element, ticket } to focus, or null when everything is answered
      validate() {
        const invalid = [];
        this.renderTickets();

        // No bag options means the event doesn't ask the question
        const hasBag = !this.bagOptions.length || this.bagOptions.some((option) => option.checked);
        this.toggleError(this, 'bag', !hasBag);
        if (!hasBag) invalid.push({ element: this.bagOptions[0] });

        this.getTickets().forEach((ticket) => {
          const element = this.validateTicket(ticket);
          if (element) invalid.push({ element, ticket });
        });

        return invalid[0] || null;
      }

      toggleError(scope, name, show) {
        const error = scope.querySelector(`[data-error="${name}"]`);
        if (error) error.hidden = !show;

        const targets = {
          bag: '[data-group="bag"]',
          name: '[data-ticket-name]',
          answer: '[data-group="answer"]',
          other: '[data-dietary-other-input]',
        };
        const target = targets[name] && scope.querySelector(targets[name]);
        if (target) this.setInvalid(target, show);
      }

      setInvalid(element, invalid) {
        if (invalid) {
          element.setAttribute('aria-invalid', 'true');
        } else {
          element.removeAttribute('aria-invalid');
        }
      }
    }
  );
}
